import { useEffect, useState } from "preact/hooks";
import { useLocation } from "preact-iso";
import "./AppDownloadBanner.scss";

const DISMISS_KEY = "ezc_app_banner_dismissed_at";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MOBILE_BREAKPOINT_PX = 600;

const APP_STORE_URL = "https://apps.apple.com/us/app/ez-coupons/id6744087809";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.retailconsultingteam.ezcoupons";

// Routes where the mobile pop-up should appear. Desktop bottom banner shows everywhere.
const MOBILE_POPUP_ROUTES = ["/", "/login"];

function shouldShowBanner(): boolean {
  try {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (!dismissedAt) return true;
    const when = parseInt(dismissedAt, 10);
    if (isNaN(when)) return true;
    return Date.now() - when > DISMISS_DURATION_MS;
  } catch {
    return true;
  }
}

export function AppDownloadBanner() {
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (shouldShowBanner()) setVisible(true);
    }, 400);
    return () => clearTimeout(t);
  }, []);

  const path = location.path || (typeof window !== "undefined" ? window.location.pathname : "/");
  const showAsPopup = visible && isMobile && MOBILE_POPUP_ROUTES.includes(path);

  // Lock body scroll while the pop-up is open so the dimmed background stays put.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (showAsPopup) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
      };
    }
  }, [showAsPopup]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  if (showAsPopup) {
    return (
      <div class="app-download-popup-overlay" onClick={dismiss}>
        <div
          class="app-download-popup"
          role="dialog"
          aria-modal="true"
          aria-label="Download the EZ Coupons app"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            class="app-download-popup__close"
            onClick={dismiss}
            aria-label="Dismiss app download pop-up"
          >
            ×
          </button>
          <div class="app-download-popup__icon" aria-hidden="true">
            <i class="fas fa-mobile-alt"></i>
          </div>
          <div class="app-download-popup__headline">Get the EZ Coupons App</div>
          <div class="app-download-popup__subtitle">
            Save on local deals right from your phone — faster, easier, and built for mobile.
          </div>
          <div class="app-download-popup__buttons">
            <a
              class="app-download-popup__btn app-download-popup__btn--apple"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fab fa-apple" aria-hidden="true"></i>
              <span>App Store</span>
            </a>
            <a
              class="app-download-popup__btn app-download-popup__btn--google"
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fab fa-google-play" aria-hidden="true"></i>
              <span>Google Play</span>
            </a>
          </div>
          <button class="app-download-popup__later" onClick={dismiss}>
            Maybe later
          </button>
        </div>
      </div>
    );
  }

  // Desktop (and any non-pop-up route on mobile): hide on mobile non-pop-up routes,
  // keep the existing bottom banner everywhere on desktop.
  if (isMobile) return null;

  return (
    <div class="app-download-banner" role="complementary" aria-label="Download the EZ Coupons app">
      <button
        class="app-download-banner__close"
        onClick={dismiss}
        aria-label="Dismiss app download banner"
      >
        ×
      </button>
      <div class="app-download-banner__content">
        <div class="app-download-banner__text">
          <div class="app-download-banner__headline">Get the EZ Coupons app</div>
          <div class="app-download-banner__subtitle">
            Save on local deals right from your phone
          </div>
        </div>
        <div class="app-download-banner__buttons">
          <a
            class="app-download-banner__btn app-download-banner__btn--apple"
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i class="fab fa-apple" aria-hidden="true"></i>
            <span>App Store</span>
          </a>
          <a
            class="app-download-banner__btn app-download-banner__btn--google"
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i class="fab fa-google-play" aria-hidden="true"></i>
            <span>Google Play</span>
          </a>
        </div>
      </div>
    </div>
  );
}
